import BTagList from "./BTagList";

export default class BProfileArticleFilter
{
	constructor(el)
	{
		this.el = el;
	}

	static className = () => `profile-article-filter`;
	static selector = () => `.${this.className()}`;
	static loadingClassName = () => `${BProfileArticleFilter.className()}--loading`;

	static initialize()
	{
		this.addEventListeners();
		// Initialize tag titles and counts on page load
		document.querySelectorAll(BProfileArticleFilter.selector()).forEach(el => {
			const filter = new BProfileArticleFilter(el);
			filter.initializeTagTitles();
			filter.updateTagsFromVisibleArticles();
			filter.updatePrioritySections();
		});
	}

	static addEventListeners()
	{
		document.addEventListener("click", event => {
			const buttonSelector = `.${BProfileArticleFilter.className()}__tag-button`;
			if (event.target.matches(`${buttonSelector}, ${buttonSelector} *`)) {
				const filterContainer = event.target.closest(BProfileArticleFilter.selector());
				if (!filterContainer) {
					return;
				}

				const filter = new BProfileArticleFilter(filterContainer);

				const button = event.target.closest(buttonSelector);
				if (!button) {
					return;
				}

				const tagListEl = button.closest(BTagList.selector());
				if (!tagListEl) {
					return;
				}

				const tagList = new BTagList(tagListEl);

				// Reset all buttons in this tag list
				tagList.resetTag();

				// Activate the clicked button
				button.classList.add("b-tag--active");

				// If product filter changed, reset tag filter to "Vše"
				const paramName = tagListEl.dataset.param;
				if (paramName === "productId") {
					const tagFilterList = filterContainer.querySelector(`${BTagList.selector()}[data-param="tagId"]`);
					if (tagFilterList) {
						const tagFilterTagList = new BTagList(tagFilterList);
						tagFilterTagList.resetTag();
						// Activate "Vše" button in tag filter
						const allTagButton = tagFilterList.querySelector(`.${BProfileArticleFilter.className()}__tag-button.b-tag--all`);
						if (allTagButton) {
							allTagButton.classList.add("b-tag--active");
						}
					}
				}

				filter.setLoading();
				filter.applyFilters();
			}
		});
	}

	applyFilters()
	{
		const filterValues = this.getFilterValues();

		// Apply filters after 500ms loading animation
		setTimeout(() => {
			// Get all article items from the DOM
			const allArticles = this.el.querySelectorAll(".profile-article-thumbnails__item");

			// Filter articles client-side
			allArticles.forEach(articleEl => {
				const shouldShow = this.shouldShowArticle(articleEl, filterValues);
				articleEl.hidden = !shouldShow;
			});

			// Update tag filter based on visible articles
			this.updateTagsFromVisibleArticles();

			// Update priority section visibility
			this.updatePrioritySections();

			// Remove loading state
			this.unsetLoading();
		}, 500);
	}

	setLoading()
	{
		this.el.classList.add(BProfileArticleFilter.loadingClassName());
	}

	unsetLoading()
	{
		this.el.classList.remove(BProfileArticleFilter.loadingClassName());
	}

	shouldShowArticle(articleEl, filterValues)
	{
		// Parse data attributes
		const productIds = this.parseJsonAttribute(articleEl.dataset.productIds);
		const tagIds = this.parseJsonAttribute(articleEl.dataset.tagIds);
		const priority = parseInt(articleEl.dataset.priority);

		// Filter by product
		if (filterValues.productId) {
			const selectedProductId = parseInt(filterValues.productId);
			if (!productIds.includes(selectedProductId)) {
				return false;
			}
		}

		// Filter by tag
		if (filterValues.tagId) {
			const selectedTagId = parseInt(filterValues.tagId);
			if (!tagIds.includes(selectedTagId)) {
				return false;
			}
		}

		// Filter by priority
		if (filterValues.priority !== undefined && filterValues.priority !== "") {
			const selectedPriority = parseInt(filterValues.priority);
			if (priority !== selectedPriority) {
				return false;
			}
		}

		return true;
	}

	parseJsonAttribute(value)
	{
		if (!value) {
			return [];
		}
		try {
			return JSON.parse(value);
		} catch (e) {
			return [];
		}
	}

	getFilterValues()
	{
		const values = {};

		// Get all tag lists with data-param attribute
		const tagLists = this.el.querySelectorAll(`${BTagList.selector()}[data-param]`);

		tagLists.forEach(tagList => {
			const paramName = tagList.dataset.param;
			if (!paramName) return;

			// Find the active button in this tag list
			const activeButton = tagList.querySelector(`.${BProfileArticleFilter.className()}__tag-button.b-tag--active`);
			if (!activeButton) return;

			// Get the value from the button's data-value attribute
			const value = activeButton.dataset.value;
			if (value) {
				values[paramName] = value;
			}
		});

		return values;
	}

	initializeTagTitles()
	{
		// Find the tag list for "Tématické okruhy" (tagId filter)
		const tagList = this.el.querySelector(`${BTagList.selector()}[data-param="tagId"]`);
		if (!tagList) {
			return;
		}

		// Extract all tag titles from initial server-side render and store them
		const tagTitles = new Map();
		const existingButtons = tagList.querySelectorAll(`.${BProfileArticleFilter.className()}__tag-button`);

		existingButtons.forEach(button => {
			if (!button.classList.contains("b-tag--all")) {
				const tagId = parseInt(button.dataset.value);
				if (tagId) {
					// Extract title (remove badge/icon text)
					const titleText = button.cloneNode(true);
					titleText.querySelectorAll(".b-tag__badge, .material-icons").forEach(el => el.remove());
					const title = titleText.textContent.trim();
					if (title) {
						tagTitles.set(tagId, title);
					}
				}
			}
		});

		// Store tag titles in a data attribute as JSON
		if (tagTitles.size > 0) {
			const titlesObj = Object.fromEntries(tagTitles);
			tagList.dataset.tagTitles = JSON.stringify(titlesObj);
		}
	}

	getTagTitles()
	{
		// Find the tag list for "Tématické okruhy" (tagId filter)
		const tagList = this.el.querySelector(`${BTagList.selector()}[data-param="tagId"]`);
		if (!tagList) {
			return new Map();
		}

		// Try to get from stored data attribute first
		if (tagList.dataset.tagTitles) {
			try {
				const titlesObj = JSON.parse(tagList.dataset.tagTitles);
				// Convert string keys back to integers
				const tagTitles = new Map();
				Object.entries(titlesObj).forEach(([tagIdStr, title]) => {
					const tagId = parseInt(tagIdStr);
					if (tagId && title) {
						tagTitles.set(tagId, title);
					}
				});
				return tagTitles;
			} catch (e) {
				// Fallback to extracting from existing buttons
			}
		}

		// Fallback: extract from existing buttons
		const tagTitles = new Map();
		const existingButtons = tagList.querySelectorAll(`.${BProfileArticleFilter.className()}__tag-button`);
		existingButtons.forEach(button => {
			if (!button.classList.contains("b-tag--all")) {
				const tagId = parseInt(button.dataset.value);
				if (tagId) {
					const titleText = button.cloneNode(true);
					titleText.querySelectorAll(".b-tag__badge, .material-icons").forEach(el => el.remove());
					const title = titleText.textContent.trim();
					if (title) {
						tagTitles.set(tagId, title);
					}
				}
			}
		});

		return tagTitles;
	}

	updateTagsFromVisibleArticles()
	{
		// Find the tag list for "Tématické okruhy" (tagId filter)
		const tagList = this.el.querySelector(`${BTagList.selector()}[data-param="tagId"]`);
		if (!tagList) {
			return;
		}

		// Get currently selected product filter (but not tag/priority filters for counting)
		const filterValues = this.getFilterValues();
		const selectedProductId = filterValues.productId ? parseInt(filterValues.productId) : null;

		// Get all articles that match the product filter (ignore tag/priority filters for tag counting)
		const allArticles = this.el.querySelectorAll(".profile-article-thumbnails__item");
		const articlesForTagCounting = Array.from(allArticles).filter(articleEl => {
			// If product filter is active, only count articles matching that product
			if (selectedProductId) {
				const productIds = this.parseJsonAttribute(articleEl.dataset.productIds);
				return productIds.includes(selectedProductId);
			}
			return true;
		});

		// Get tag titles from stored data or existing buttons
		const tagTitles = this.getTagTitles();

		// Count tags from articles matching product filter
		const tagCounts = new Map();
		const tagReadCounts = new Map();

		articlesForTagCounting.forEach(articleEl => {
			const tagIds = this.parseJsonAttribute(articleEl.dataset.tagIds);
			const isRead = articleEl.querySelector(".profile-article-thumbnail--is-read") !== null;

			tagIds.forEach(tagId => {
				if (!tagCounts.has(tagId)) {
					tagCounts.set(tagId, 0);
					tagReadCounts.set(tagId, 0);
				}
				tagCounts.set(tagId, tagCounts.get(tagId) + 1);
				if (isRead) {
					tagReadCounts.set(tagId, tagReadCounts.get(tagId) + 1);
				}
			});
		});

		// Find the currently active button to preserve its state
		const activeButton = tagList.querySelector(`.${BProfileArticleFilter.className()}__tag-button.b-tag--active`);
		const activeValue = activeButton ? activeButton.dataset.value : null;
		const isAllActive = activeButton && activeButton.classList.contains("b-tag--all");

		// Remove all existing tag buttons except "Vše"
		const existingItems = tagList.querySelectorAll(".b-tag-list__item");
		existingItems.forEach(item => {
			const button = item.querySelector(`.${BProfileArticleFilter.className()}__tag-button`);
			if (button && !button.classList.contains("b-tag--all")) {
				item.remove();
			}
		});

		// Add tag buttons for tags that appear in visible articles
		const sortedTags = Array.from(tagCounts.entries())
			.sort((a, b) => b[1] - a[1]); // Sort by count descending

		sortedTags.forEach(([tagId, total]) => {
			const totalRead = tagReadCounts.get(tagId) || 0;
			const title = tagTitles.get(tagId) || `Tag ${tagId}`;

			const listItem = document.createElement("li");
			listItem.className = "b-tag-list__item";

			const button = document.createElement("button");
			button.className = `b-tag ${BProfileArticleFilter.className()}__tag-button`;
			button.type = "button";
			button.dataset.value = tagId;
			button.textContent = title;

			// Add badge or done icon
			if (totalRead === total && total > 0) {
				const icon = document.createElement("span");
				icon.className = "material-icons icon icon--right";
				icon.textContent = "done";
				button.appendChild(icon);
			} else if (total > 0) {
				const badge = document.createElement("span");
				badge.className = "b-tag__badge";
				badge.textContent = `${totalRead}/${total}`;
				button.appendChild(badge);
			}

			// Restore active state if this tag was previously selected
			if (!isAllActive && activeValue && tagId.toString() === activeValue.toString()) {
				button.classList.add("b-tag--active");
			}

			listItem.appendChild(button);
			tagList.appendChild(listItem);
		});

		// If "Vše" was active, ensure it stays active
		if (isAllActive) {
			const allButton = tagList.querySelector(`.${BProfileArticleFilter.className()}__tag-button.b-tag--all`);
			if (allButton) {
				allButton.classList.add("b-tag--active");
			}
		}

		// Show/hide the filter group based on number of tags
		// Show only if there are 2+ tags (matching the requirement "2 a více tématických okruhů")
		const filterGroup = tagList.closest(".profile-article-filter__filter-group");
		if (filterGroup) {
			if (sortedTags.length >= 2) {
				filterGroup.style.display = "";
			} else {
				filterGroup.style.display = "none";
			}
		}
	}

	updatePrioritySections()
	{
		// Get all priority sections
		const prioritySections = this.el.querySelectorAll(".profile-article-priority-section");

		// Count how many sections have visible articles
		let visibleSectionCount = 0;

		prioritySections.forEach(section => {
			// Count visible articles in this section
			const articles = section.querySelectorAll(".profile-article-thumbnails__item");
			const visibleCount = Array.from(articles).filter(article => !article.hidden).length;

			// Show/hide section based on visible articles
			section.hidden = visibleCount === 0;

			if (!section.hidden) {
				visibleSectionCount++;
			}
		});

		// Hide priority titles if there's only one visible priority section
		prioritySections.forEach(section => {
			if (section.hidden) {
				return;
			}

			const title = section.querySelector("h3.title--4");
			if (title) {
				title.hidden = visibleSectionCount === 1;
			}
		});
	}
}
